/**
 * Analysis - Qos Store
 *
 * @since 2018-08-20
 * @author ms1124.kim
 *
 */
import JSONBigInt from 'json-bigint'
const analysisApiPath = '/api/an/analysis'
const qosProfileApiPath = '/api/po/qos/profiles'
const qosInterfaceApiPath = '/api/po/qos/able/interfaces'

export const state = () => ({
  queues: [],
  profiles: [],
  interfaces: [],
  // TAMS
  device: void 0
})

export const mutations = {
  /*
    이름: setQueue
    설명: Qos 큐를 스토어에 저장한다.
  */
  setQueue (state, queue) {
    state.queues = queue
  },
  /*
    이름: setProfiles
    설명: 프로파일 목록을 스토어에 저장한다.
  */
  setProfiles (state, profiles) {
    state.profiles = profiles
  },
  /*
    이름: setInterfaces
    설명: 인터페이스 목록을 스토어에 저장한다.
  */
  setInterfaces (state, interfaces) {
    state.interfaces = interfaces
  },
  /*
    이름: initQosStore
    설명: Qos 스토어 데이터를 초기화한다.
  */
  setInitQosStore (state) {
    state.queues = []
    state.profiles = []
    state.interfaces = []
    // TAMS
    state.device = void 0
  },
  // TAMS
  setDevice (state, device) {
    state.device = device
  }
}

export const actions = {
  /*
    이름: startAnalysisData
    설명: 분석 데이터 조회를 시작한다.
  */
  async startAnalysisData ({commit, state}, analysisParams) {
    let result = {
      code: 'ok',
      result: {}
    }

    try {
      result = await this.$axios.$post(`${analysisApiPath}/start`, analysisParams, {
        loading: false,
        successAlert: false
      })
    } catch (e) {
      result.code = 'nok'
      result.reason = e
    }

    return result
  },
  /*
    이름: getAnalysisStatus
    설명: 조회중인 분석 데이터의 상태를 가져온다.
  */
  async getAnalysisStatus ({commit, state}, {requestId}) {
    let result = {
      code: 'ok',
      result: {}
    }

    try {
      result = await this.$axios.$get(`${analysisApiPath}/${requestId}/status`, {
        loading: false,
        successAlert: false
      })
    } catch (e) {
      result.code = 'nok'
      result.reason = e
    }

    return result
  },
  /*
    이름: getAnalysisData
    설명: 분석이 중지 / 완료된 데이터를 가져온다.
  */
  async getAnalysisData ({commit, state}, {requestId}) {
    let result = {
      code: 'ok',
      result: {}
    }

    try {
      result = await this.$axios.$get(`${analysisApiPath}/${requestId}/result`, {
        loading: false,
        successAlert: false,
        transformResponse: [
          (data) => {
            return JSONBigInt.parse(data)
          }
        ]
      })
    } catch (e) {
      result.code = 'nok'
      result.reason = e
    }

    return result
  },
  /*
    이름: stopAnalysisData
    설명: 조회중인 작업을 중지하고 현재까지의 결과를 리턴한다.
  */
  async stopAnalysisData ({commit, state}, {requestId}) {
    let result = {
      code: 'ok',
      result: {}
    }

    try {
      result = await this.$axios.$delete(`${analysisApiPath}/${requestId}${'/stop'}`, {
        loading: false,
        successAlert: false,
        transformResponse: [
          (data) => {
            return JSONBigInt.parse(data)
          }
        ]
      })
    } catch (e) {
      result.code = 'nok'
      result.reason = e
    }

    return result
  },
  /*
    이름: endAnalysisData
    설명: 조회 작업이 완료되면 호출된다. 서버의 리소스를 해제하는 명령어이므로 반드시 호출한다.
  */
  async endAnalysisData ({commit, state}, {requestId}) {
    let result = {
      code: 'ok',
      result: {}
    }

    try {
      result = await this.$axios.$delete(`${analysisApiPath}/${requestId}${'/end'}`, {
        loading: false,
        successAlert: false
      })
    } catch (e) {
      result.code = 'nok'
      result.reason = e
    }

    return result
  },
  /*
    이름: getQosQueue
    설명: Qos 큐 목록을 불러온다.
  */
  async getQosQueue ({state, commit}, {profileId}) {
    const res = {
      code: 'ok'
    }

    try {
      // 인터페이스 목록 불러오기
      const {code, result} = await this.$axios.$get(`/api/po/qos/profiles/${profileId}/queues`)

      // 요청이 성공하면 프로파일 목록을 스토어에 저장한다.
      if (code === 'ok') {
        commit('setQueue', result)
      }
    } catch (e) {
      res.code = 'nok'
      res.reason = e
    }

    return res
  },
  /*
    이름: getQosProfiles
    설명: Qos 프로파일 목록을 불러온다.
  */
  async getQosProfiles ({state, commit}) {
    const res = {
      code: 'ok'
    }

    try {
      // 인터페이스 목록 불러오기
      const {code, result} = await this.$axios.$get(qosProfileApiPath)

      // 요청이 성공하면 프로파일 목록을 스토어에 저장한다.
      if (code === 'ok') {
        commit('setProfiles', result)
      }
    } catch (e) {
      res.code = 'nok'
      res.reason = e
    }

    return res
  },
  /*
    이름: getQosInterfaces
    설명: Qos가 설정된 인터페이스 목록을 가져온다.
  */
  async getQosInterfaces ({state, commit}) {
    const res = {
      code: 'ok'
    }

    try {
      // 인터페이스 목록 불러오기
      const {code, result} = await this.$axios.$get(qosInterfaceApiPath)

      // 요청이 성공하면 인터페이스 목록 데이터 포맷 변경
      if (code === 'ok') {
        const ethList = []
        for (const ethInfo of result) {
          // 인터페이스 이름 가져오기
          const ethName = ethInfo.ifc_name

          // 인터페이스 목록 추가
          ethList.push(ethName)
        }

        // 인터페이스 목록 스토어에 저장
        commit('setInterfaces', ethList)
      }
    } catch (e) {
      res.code = 'nok'
      res.reason = e
    }

    return res
  },
  // TAMS
  // 2. QoS 분석 - 관리장비 송신 인터페이스 조회
  async getQosInterfacesId ({state, commit}, payload) {
    try {
      commit('setInterfaces', []) // 초기화
      commit('setProfiles', []) // 초기화
      commit('setQueue', []) // 초기화
      const res = await this.$axios.$get(`/api/an/qos/interfaces/${payload.device_id}`)

      if (res.code === 'ok') {
        let ethList = []
        ethList = res.result.map(item => {
          return item.ifc_name
        })
        commit('setInterfaces', ethList)
      }
      return res
    } catch (e) {
      return {code: 'error', errors: e}
    }
  },
  // 3. QoS 분석 - 관리장비 QoS 프로파일 조회
  async getQosProfilesId ({state, commit}, payload) {
    try {
      commit('setProfiles', []) // 초기화
      commit('setQueue', []) // 초기화
      const res = await this.$axios.$get(`/api/an/qos/profiles/${payload.device_id}`)
      if (res.code === 'ok') {
        commit('setProfiles', res.result)
      }
      return res
    } catch (e) {
      return {code: 'error', errors: e}
    }
  },
  // 4. QoS 분석 - 관리장비 QoS 프로파일 queue 조회
  async getQosQueueId ({state, commit}, payload) {
    try {
      commit('setQueue', []) // 초기화
      const res = await this.$axios.$get(`/api/an/qos/profiles/queues/${payload.profile_id}/${payload.device_id}`)
      if (res.code === 'ok') {
        commit('setQueue', res.result)
      }
      return res
    } catch (e) {
      return {code: 'error', errors: e}
    }
  }
}
export const getters = {}
export const strict = false
